'use client'

import { CANARY_MISSION } from '@/enum/enum'
import { useEffect, useRef, useState } from 'react'
import { TitleAndInput } from '../reportIndividual/inputFields'
import axios from 'axios'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { z } from 'zod'
import { DonateSchema } from './schema'
import { convertDonateToFormData } from './utils'
import { SuccessPage } from './successPage'
import { server_name, spinner } from '../reportingTool/reportingUtils'

type DonateForm = z.infer<typeof DonateSchema>

export const Donate = () => {
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({})
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [donationAmount, setDonationAmount] = useState('')
  const [message, setMessage] = useState('')
  const [honeypotUsername, setHoneypotUsername] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const captchaRef = useRef<any>(null)
  const pendingDonationRef = useRef<DonateForm | null>(null)

  useEffect(() => {
    if (isSubmitted) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [isSubmitted])

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const amount = searchParams.get('amount')

    if (amount) {
      setDonationAmount(amount)
    }
  }, [])

  const clearError = (fieldName: string) => {
    setFormErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors }
      delete updatedErrors[fieldName]
      return updatedErrors
    })
  }

  const onClickSubmit = () => {
    const data = {
      firstName,
      lastName,
      email,
      phoneNumber,
      zipCode,
      donationAmount,
      message,
    }

    const safeParsedData = DonateSchema.safeParse(data)

    if (!safeParsedData.success) {
      const errors: { [key: string]: string } = {}

      safeParsedData.error.errors.forEach((err) => {
        const fieldName = err.path[0]
        if (typeof fieldName === 'string') {
          errors[fieldName] = err.message
        }
      })

      setFormErrors(errors)

      const firstErrorKey = Object.keys(errors)[0]
      if (firstErrorKey) {
        const errorElement = document.getElementsByName(firstErrorKey)?.[0]
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          errorElement.focus({ preventScroll: true })
        }
      }

      return
    }

    pendingDonationRef.current = safeParsedData.data
    setIsLoading(true)
    captchaRef?.current?.execute()
  }

  const onVerify = async (token: string) => {
    try {
      setIsLoading(true)

      const pendingDonation = pendingDonationRef.current

      if (!pendingDonation) {
        setIsLoading(false)
        return
      }

      const formData = convertDonateToFormData(pendingDonation, token)
      formData.append('username', honeypotUsername)

      if (!server_name) throw new Error('Server name is not set')

      await axios.post(`${server_name}/submit-donation`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      pendingDonationRef.current = null
      setIsSubmitted(true)
    } catch (error) {
      setIsLoading(false)

      let errorMessage
      let errorStatus
      let errorDetails

      if (axios.isAxiosError(error)) {
        errorStatus = error.response?.status
        errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred'
        errorDetails = error.response?.data?.details || ''
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

      console.error('errorMessage', errorMessage)
      alert(`Error ${errorStatus}: ${errorMessage} ${errorDetails}`)
    }
  }

  const renderSpinner = (): JSX.Element | null => {
    if (!isLoading) return null
    return spinner
  }

  const renderHeader = () => {
    return (
      <div className="text-[30px] font-bold leading-[45px] md:text-[40px] md:leading-[60px] md:mb-[25px]">
        <div className="mb-[10px] text-center">Donate to {CANARY_MISSION.NAME}</div>
      </div>
    )
  }

  const renderFormContent = () => {
    return (
      <div>
        <HCaptcha
          size="invisible"
          ref={captchaRef}
          sitekey="366080e6-99da-4baa-a5a4-7b6e89f09bee"
          onVerify={onVerify}
          onClose={() => setIsLoading(false)}
          onError={(error) => {
            console.error('[hCaptcha] Error:', error)
            setIsLoading(false)
          }}
          onExpire={() => setIsLoading(false)}
        />

        <div className="mx-auto">
          <div className="m-2 bg-gray-100 border-[1px] border-gray-600 rounded-[8px] p-4">
            <div className="flex flex-wrap md:gap-4">
              <TitleAndInput
                title="First Name"
                required={true}
                type="text"
                inputValue={firstName}
                name="firstName"
                placeholder="Enter first name"
                handleFieldChange={(e) => {
                  setFirstName(e.target.value)
                  clearError('firstName')
                }}
                errorText={formErrors.firstName}
              />

              <TitleAndInput
                title="Last Name"
                required={true}
                type="text"
                inputValue={lastName}
                name="lastName"
                placeholder="Enter last name"
                handleFieldChange={(e) => {
                  setLastName(e.target.value)
                  clearError('lastName')
                }}
                errorText={formErrors.lastName}
              />
            </div>

            <div className="flex flex-wrap md:gap-4">
              <TitleAndInput
                title="Email"
                required={true}
                type="email"
                inputValue={email}
                name="email"
                placeholder="Enter email address"
                handleFieldChange={(e) => {
                  setEmail(e.target.value)
                  clearError('email')
                }}
                errorText={formErrors.email}
              />

              <TitleAndInput
                title="ZIP Code"
                required={true}
                type="text"
                inputValue={zipCode}
                name="zipCode"
                placeholder="Enter ZIP code"
                handleFieldChange={(e) => {
                  setZipCode(e.target.value)
                  clearError('zipCode')
                }}
                errorText={formErrors.zipCode}
              />

              <TitleAndInput
                title="Phone Number"
                required={false}
                type="text"
                inputValue={phoneNumber}
                name="phoneNumber"
                placeholder="Enter phone number"
                handleFieldChange={(e) => {
                  setPhoneNumber(e.target.value)
                  clearError('phoneNumber')
                }}
                errorText={formErrors.phoneNumber}
              />
            </div>

            <div className="w-full md:w-[300px]">
              <TitleAndInput
                title="Donation Amount (USD)"
                required={true}
                type="text"
                inputValue={donationAmount}
                name="donationAmount"
                placeholder="Enter donation amount"
                handleFieldChange={(e) => {
                  setDonationAmount(e.target.value)
                  clearError('donationAmount')
                }}
                errorText={formErrors.donationAmount}
              />
            </div>

            <div className="flex flex-col mb-3">
              <div className="text-[16px] font-medium leading-[24px] mb-1">
                Message for {CANARY_MISSION.NAME}
              </div>
              <textarea
                className="min-h-[100px]"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value)
                  clearError('message')
                }}
                placeholder={`Enter a message for ${CANARY_MISSION.NAME}`}
                name="message"
              />
              <div className="text-red-500 italic text-sm mt-2">{formErrors.message}</div>
            </div>

            <div className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10">
              <div>UserName</div>
              <input
                type="text"
                id="username"
                value={honeypotUsername}
                onChange={(e) => setHoneypotUsername(e.target.value)}
                placeholder="Enter Your Username"
                name="username"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-center w-full items-center">
          <button
            type="button"
            disabled={isLoading}
            className="!text-black flex flex-row items-center justify-center bg-feYellow w-[100px] h-[40px] px-[5px] py-[3px] m-[5px] border-[1px] rounded-[8px]"
            onClick={onClickSubmit}
          >
            <div>{isLoading ? 'Submitting' : 'Submit'}</div>
            {renderSpinner()}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {renderHeader()}
      <div className="mt-4 w-full">
        {isSubmitted ? (
          <SuccessPage />
        ) : (
          renderFormContent()
        )}
      </div>
    </div>
  )
}